import * as yup from 'yup';
import { AnySchema } from 'yup';
import { ApiTypesEnum } from '@api/ringil-api.v2.tsx';
import { LocalityEntryModeEnum, NewExpeditionForm } from '@app/pages/expedition/create/types.ts';
import { createOrdersComponentValidationSchema, OrdersConfig } from '@components/framework/orders/validation.ts';
import { timeslotValidationSchema } from '@components/obsolete/TimeslotWithDefaults/ManageTimeslot.tsx';
import { ladenFactorValueValidation } from '@utils/expeditionUtils.ts';
import { CargoTypeCodeEnum } from '@utils/constants.ts';

type Translate = (key: string, defaultText?: string, data?: any, count?: number) => string;

export interface ExpeditionValidationDeps {
  tr: Translate;
  /** Orders config from `expeditionConfig(...)` — decides which cargo dimensions are mandatory. */
  ordersConfig: OrdersConfig;
  ladenFactorEnabled: boolean;
  /** RD-2977: expedition module setting `folders.required` — at least one folder must be chosen. */
  folderRequired?: boolean;
}

const additionalPersonsValidation = (tr: Translate) =>
  yup.array().of(
    yup.object().shape({
      email: yup.string().email(tr(`validation.valid_email`, 'Value has to be valid email address.')).required(),
    }),
  );

const manuallyEnteredLocationValidation = (tr: Translate) =>
  yup.object().shape({
    name: yup.string().required(),
    city: yup.string().required(),
    streetNr: yup.string().required(),
    countryCode3: yup.string().required(),
    postalCode: yup.string().required(),
    timezoneId: yup.string().required(tr('validation.required', 'This field is required')),
    mainContactEmail: yup.string().nullable().email(tr(`validation.valid_email`, 'Value has to be valid email address.')),
    additionalPersons: additionalPersonsValidation(tr),
  });

/**
 * Validation for the create-shipment form.
 *
 * Returned as a *factory* rather than a schema: `useForm` hands the data being validated to the
 * factory, which is what the Dachser order-description rule needs — its limit depends on which
 * carriers are selected, i.e. on another part of the same form.
 */
export const buildExpeditionValidationSchema =
  ({ tr, ordersConfig, ladenFactorEnabled, folderRequired = false }: ExpeditionValidationDeps) =>
    (values: NewExpeditionForm): AnySchema => {
      const orderSchema = createOrdersComponentValidationSchema(ordersConfig, false, tr).shape({
        orderDescription: yup
          .string()
          .nullable()
          .notRequired()
          .test(
            'dachser-length',
            tr('CreateExpedition.dachserOrderDescriptionTooLong', 'Order description must be max 20 characters for Dachser'),
            function(value) {
              if (!value) return true;

              const hasDachser = values?.distribution?.selectedItems?.some(item => item.apiTypes?.includes(ApiTypesEnum.DACHSER));

              return !(hasDachser && value.length > 20);
            },
          ),
      });

      return yup.object().shape({
        locations: yup.array().of(
          yup.object().shape({
            localityMode: yup.string().required(),
            timezoneId: yup.string().required(tr('validation.required', 'This field is required')),
            timeslots: timeslotValidationSchema(),
            manualAddress: yup.object().when('localityMode', {
              is: LocalityEntryModeEnum.MANUALLY,
              then: manuallyEnteredLocationValidation(tr),
              otherwise: yup.object().nullable(),
            }),
            additionalPersons: additionalPersonsValidation(tr).nullable(),
            selectedPerson: yup.object().nullable(),
            companyLocationId: yup.string().when('localityMode', {
              is: LocalityEntryModeEnum.FROM_LOCATIONS,
              then: yup.string().required().min(1),
              otherwise: yup.string().nullable(),
            }),
            orders: yup.array().nullable().min(0).of(orderSchema),
          }),
        ),

        distribution: yup
          .object()
          .shape({
            distributionType: yup.string(),
            price: yup.object().when('distributionType', {
              is: (type: string) => !!type, // any distribution type at all
              then: yup
                .object()
                .shape({
                  price: yup
                    .number()
                    .typeError(tr('CreateExpedition.priceShouldBeNumber', 'Price should be number'))
                    .transform(value => (isNaN(value) ? undefined : value))
                    .nullable()
                    .required(tr('CreateExpedition.mandatoryPrice', 'Price is required')),
                  currency: yup.string().required(tr('CreateExpedition.mandatoryCurrency', 'Currency is required')),
                })
                .required(tr('CreateExpedition.mandatoryPrice', 'Price is required')),
              otherwise: yup.object().nullable(),
            }),
            selectedItems: yup.array().when('distributionType', {
              is: (type: string) => type === 'FIXED' || type === 'SPOT' || type === 'ASSIGN',
              then: yup
                .array()
                .min(1, tr('CreateExpedition.mandatoryCarriers', 'Please select at least one carrier'))
                .required(tr('CreateExpedition.mandatoryCarriers', 'Please select at least one carrier'))
                // FedEx Express Freight needs the booking confirmation number from FedEx Freight Customer Service
                .test(
                  'fedex-freight-booking',
                  tr('FedexProductTerms.bookingConfirmationRequired', 'FedEx booking confirmation number is required for freight services'),
                  items => !items?.some(item => item._fedexProductTypeCode?.includes('_FREIGHT') && !item._fedexBookingConfirmationNumber?.trim()),
                )
                // several FedEx accounts allowed for the route - the user has to pick one
                .test(
                  'fedex-account',
                  tr('FedexProductTerms.accountRequired', 'Select the FedEx account the shipment goes on'),
                  items => !items?.some(item => item._fedexAccountRequired && !item._fedexAccountId),
                ),
              otherwise: yup.array().nullable(),
            }),
            responseDeadlineOffsetValue: yup.number().when(['distributionType', 'responseDeadlineMode'], {
              is: (type: string, mode: string) => (type === 'FIXED' || type === 'SPOT') && mode !== 'absolute',
              then: yup
                .number()
                .typeError(tr('InputDeadline.required', 'Offer deadline is required'))
                .min(1, tr('InputDeadline.required', 'Offer deadline is required'))
                .required(tr('InputDeadline.required', 'Offer deadline is required')),
              otherwise: yup.number().nullable(),
            }),
            responseDeadlineLocalTime: yup.mixed().when(['distributionType', 'responseDeadlineMode'], {
              is: (type: string, mode: string) => (type === 'FIXED' || type === 'SPOT') && mode === 'absolute',
              then: yup.mixed().required(tr('InputDeadline.required', 'Offer deadline is required')),
              otherwise: yup.mixed().nullable(),
            }),
            ladenFactorValue: ladenFactorValueValidation(
              {
                shouldBeNumber: tr('CreateExpedition.ladenFactorShouldBeNumber', 'Laden factor should be a number'),
                required: tr('CreateExpedition.mandatoryLadenFactor', 'Laden factor is required'),
              },
              ladenFactorEnabled,
              type => type === 'FIXED' || type === 'SPOT',
            ),
          })
          .nullable(),

        // Shipment type is optional (RD-2961): an empty value is saved as "no type".
        expeditionType: yup.string().nullable(),

        // RD-2977: the company can require every shipment to be filed in at least one folder.
        folders: folderRequired
          ? yup.array().min(1, tr('CreateExpedition.mandatoryFolder', 'Folder is required')).required(tr('CreateExpedition.mandatoryFolder', 'Folder is required'))
          : yup.array().nullable(),

        // The shipment parameters that start out pre-filled with a default. Emptying one — which is
        // what a merge of shipments that disagreed does — has to be an error rather than an opening
        // for the backend to put a value back in that reflects nothing the user chose.

        cargoType: yup
          .object()
          .nullable()
          .required(tr('CreateExpedition.mandatoryCargoType', 'Cargo type is required'))
          .shape({
            cargoTypeCode: yup.string().nullable().required(tr('CreateExpedition.mandatoryCargoType', 'Cargo type is required')),
            adrWeight: yup.number().when('cargoTypeCode', {
              is: CargoTypeCodeEnum.ADR_LQ,
              then: schema => schema.required(),
              otherwise: schema => schema.notRequired().nullable(),
            }),
          }),
      });
    };